import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components";
import api from "../utils/apiClient";
import Fuse from "fuse.js";
import LoadingBar from 'react-top-loading-bar';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(3px);
  -webkit-backdrop-filter: blur(3px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
  box-sizing: border-box;
`;

const ModalContainer = styled.div`
  background: #ffffff;
  border-radius: 16px;
  max-width: 480px;
  width: 100%;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  position: relative;
  overflow: hidden;
  animation: modalIn 0.18s ease-out;

  @keyframes modalIn {
    from { opacity: 0; transform: translateY(12px) scale(0.98); }
    to { opacity: 1; transform: translateY(0) scale(1); }
  }
`;

const CloseButton = styled.button`
  background: #f1f3f5;
  border: 1px solid #e4e7eb;
  color: #6b7280;
  position: absolute;
  top: 14px;
  right: 14px;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  line-height: 1;
  padding: 0;
  z-index: 2;
  transition: background 0.15s ease, color 0.15s ease, transform 0.1s ease;

  &:hover {
    background: #e4e7eb;
    color: #1f2937;
    transform: scale(1.05);
  }
`;

const ModalHeader = styled.div`
  padding: 20px 24px 16px;
  display: flex;
  align-items: center;
  gap: 14px;
  border-bottom: 1px solid #f1f3f5;
  padding-right: 56px;
`;

const HeaderThumb = styled.img`
  width: 56px;
  height: 56px;
  object-fit: cover;
  border-radius: 10px;
  background: #f1f3f5;
  flex-shrink: 0;
  border: 1px solid #e4e7eb;
`;

const HeaderText = styled.div`
  flex: 1;
  min-width: 0;
`;

const HeaderSubtitle = styled.p`
  font-size: 0.72rem;
  font-weight: 600;
  color: #6b7280;
  margin: 0;
  padding: 0;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const HeaderTitle = styled.p`
  font-size: 1rem;
  font-weight: 700;
  color: #1f2937;
  margin: 3px 0 0;
  padding: 0;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ModalBody = styled.div`
  padding: 20px 24px 22px;
`;

const SectionTitle = styled.h2`
  font-size: 1.05rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 4px 0;
  padding: 0;
`;

const SectionHint = styled.p`
  font-size: 0.82rem;
  color: #6b7280;
  margin: 0 0 14px 0;
  padding: 0;
`;

const SearchWrapper = styled.div`
  position: relative;
  margin-bottom: 10px;

  input {
    width: 100%;
    padding: 11px 14px 11px 38px;
    background: #f8f9fa url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><circle cx='11' cy='11' r='7'/><path d='M21 21l-4.35-4.35'/></svg>") no-repeat 12px center;
    background-size: 16px 16px;
    border: 1px solid #e4e7eb;
    border-radius: 8px;
    font-size: 0.92rem;
    color: #1f2937;
    box-sizing: border-box;
    transition: border-color 0.15s ease, background-color 0.15s ease;
    font-family: inherit;

    &:focus {
      outline: none;
      border-color: #db3a34;
      background-color: #ffffff;
    }

    &::placeholder {
      color: #9ca3af;
    }
  }

  .suggestions-list-mod {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    padding: 4px;
    margin: 0;
    background: #ffffff;
    border: 1px solid #e4e7eb;
    border-radius: 8px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
    list-style: none;
    max-height: 180px;
    overflow-y: auto;
    z-index: 10;
  }

  .suggestion-item {
    padding: 8px 12px;
    cursor: pointer;
    border-radius: 6px;
    font-size: 0.9rem;
    color: #374151;
    transition: background-color 0.12s ease;
    text-align: left;

    &:hover {
      background: #f1f3f5;
      color: #1f2937;
    }
  }
`;

const UserForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  background: #f8f9fa;
  border: 1px solid #e4e7eb;
  border-radius: 8px;
  margin-top: 12px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 14px;
  background: #ffffff;
  border: 1px solid #e4e7eb;
  border-radius: 8px;
  font-size: 0.92rem;
  color: #1f2937;
  box-sizing: border-box;
  font-family: inherit;
  transition: border-color 0.15s ease;

  &:focus {
    outline: none;
    border-color: #db3a34;
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 18px;
`;

const ConfirmButton = styled.button`
  background: #db3a34;
  color: white;
  border: none;
  padding: 11px 20px;
  border-radius: 8px;
  font-size: 0.92rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s ease, transform 0.1s ease;

  &:hover {
    background: #b81414;
  }

  &:active {
    transform: scale(0.99);
  }
`;

const AddButton = styled.button`
  background: transparent;
  color: #374151;
  border: 1px solid #e4e7eb;
  padding: 11px 20px;
  border-radius: 8px;
  font-size: 0.92rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: #f8f9fa;
    border-color: #d1d5db;
    color: #1f2937;
  }
`;

const Modal = ({ show, onClose, onConfirm, carroid, carro }) => {
  const [nombres, setnombres] = useState([]);
  const [input, setInput] = useState("");
  const [, setSelectedName] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [fulluser, setfulluser] = useState([]);
  const [userId, setUserid] = useState("");
  const [registrado, setregistrado] = useState(true);
  const [showUserForm, setShowUserForm] = useState(false);

  const ref = useRef(null);

  const backgroundurl = carro.imagenes[0];

  const findallusernames = async () => {
    const res = await api.get("usuario");
    const data = res.data;
    const usernames = [];
    data.forEach((usuario) => {
      if (usuario.nombre) {
        usernames.push(usuario.nombre);
      }
    });
    setfulluser(data);
    setnombres(usernames);
  };

  const handleChange = (event) => {
    const value = event.target.value;
    setInput(value);
    detectInputType(value);
  };

  const fuse = new Fuse(
    nombres.map((nombre) => ({ nombre: nombre })),
    {
      threshold: 0.9,
      distance: 100,
      minMatchCharLength: 3,
      keys: ["nombre"],
    }
  );

  const detectInputType = (value) => {
    setSelectedName("");
    const valueTrim = value.trim();
    let matches = [];
    const suggestions = fuse.search(valueTrim);
    setSuggestions(suggestions.map((suggestion) => suggestion.item.nombre));
    if (
      nombres.some((nombre) => nombre.toLowerCase() === valueTrim.toLowerCase())
    ) {
      matches.push({ type: "nombre", value: valueTrim });
    }
    if (matches.length === 0) {
      matches.push({ type: "unknown", value: value });
    }
  };

  const finduser = (username) => {
    if (!Array.isArray(fulluser)) {
      console.error("fulluser is not an array:", fulluser);
      return;
    }

    const matchedUser = fulluser.find(
      (user) => user?.nombre?.toLowerCase() === username.toLowerCase()
    );

    if (matchedUser) {
      setUserid(matchedUser._id);
    } else {
      console.warn("No user found with nombre:", username);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setInput(suggestion);
    setSuggestions([]);
    finduser(suggestion);
  };

  const [nombre, setnombre] = useState("");
  const [correo, setcorreo] = useState("");
  const [telefono, settelefono] = useState("");

  const comprar = async () => {
    ref.current.continuousStart();
    try {
      const data = {
        identifierUser: userId,
        registrado: registrado,
        nombre: nombre,
        correo: correo,
        telefono: telefono,
      };
      if (correo === "") {
        data.correo = "sincorreo@honduautos.com";
      }
      if (telefono === "") {
        data.telefono = "sintelefono";
      }
      await api.post("ventas/compra/" + carroid, data);
      onConfirm();
      onClose();
    } catch (error) {
      console.error(error);
    } finally {
      ref.current.complete();
    }
  };

  const handleAddButtonClick = () => {
    setShowUserForm(true);
    setregistrado(false);
  };

  useEffect(() => {
    if (show) {
      findallusernames();
    }
  }, [show]);

  if (!show) {
    return null;
  }

  return (
    <Overlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <LoadingBar color="#db3a34" ref={ref} className="top-loading-bar" />
        <CloseButton onClick={onClose} aria-label="Cerrar">×</CloseButton>
        <ModalHeader>
          <HeaderThumb src={backgroundurl} alt="" />
          <HeaderText>
            <HeaderSubtitle>Lote {carro.lote}</HeaderSubtitle>
            <HeaderTitle>
              {carro.marca} {carro.modelo} {carro.anio}
            </HeaderTitle>
          </HeaderText>
        </ModalHeader>
        <ModalBody>
          <SectionTitle>Añadir Comprador</SectionTitle>
          <SectionHint>
            {showUserForm
              ? "Registra los datos del nuevo comprador."
              : "Busca un comprador registrado o añade uno nuevo."}
          </SectionHint>
          {!showUserForm && (
            <SearchWrapper>
              <input
                type="text"
                id="Search"
                value={input}
                onChange={handleChange}
                placeholder="Nombre del comprador"
              />
              {suggestions.length > 0 && (
                <ul className="suggestions-list-mod">
                  {suggestions.map((suggestion, index) => (
                    <li
                      key={index}
                      onClick={() => handleSuggestionClick(suggestion)}
                      className="suggestion-item"
                    >
                      {suggestion}
                    </li>
                  ))}
                </ul>
              )}
            </SearchWrapper>
          )}
          {showUserForm && (
            <UserForm>
              <Input
                type="text"
                placeholder="Nombre completo"
                value={nombre}
                onChange={(e) => setnombre(e.target.value)}
              />
              <Input
                type="email"
                placeholder="Correo (opcional)"
                value={correo}
                onChange={(e) => setcorreo(e.target.value)}
              />
              <Input
                type="tel"
                placeholder="Teléfono (opcional)"
                value={telefono}
                onChange={(e) => settelefono(e.target.value)}
              />
            </UserForm>
          )}
          <ButtonRow>
            <ConfirmButton onClick={comprar}>Confirmar compra</ConfirmButton>
            {!showUserForm && (
              <AddButton onClick={handleAddButtonClick}>
                Usuario no registrado
              </AddButton>
            )}
          </ButtonRow>
        </ModalBody>
      </ModalContainer>
    </Overlay>
  );
};

export default Modal;
